import { useEffect, useId, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useRouterState } from '@tanstack/react-router'
/* Un icono por nombre, nunca el paquete entero: lucide-react publica más de
   mil quinientos, y un `import *` los mete todos en el paquete final. Así solo
   viaja lo que se usa aquí. */
import {
  Bell,
  CalendarCheck,
  CalendarSearch,
  ConciergeBell,
  DoorOpen,
  House,
  Settings,
  UserRound,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { obtenerConfiguracion } from '../api/configuracion'
import { fechaDeHoy } from '../rutas/busqueda-disponibilidad'
import { usePerfil } from '../sesion/usePerfil'
import { BotonEntrar } from '../sesion/BotonEntrar'
import { BotonSalir } from '../sesion/BotonSalir'
import { puedeAdministrar } from '../sesion/SoloAdministrador'
import { CLAVE_NOTIFICACIONES, consultarAvisos } from '../api/notificaciones'
import { puedeAtenderMostrador } from '../sesion/SoloMostrador'
import { textoDeError } from '../api/errores'

export const NOMBRE_SISTEMA = 'Reservación de Espacios'

/**
 * El icono de un destino del menú.
 *
 * EXISTE PARA QUE NO SE PUEDA OLVIDAR EL NOVENO. Son ocho destinos, y los tres
 * atributos que hacen que un icono sea decorativo —clase, `aria-hidden` y
 * `focusable`— habría que repetirlos en cada uno. Repetidos ocho veces se
 * copian bien; en el noveno se pega mal uno y aparece un icono que el lector de
 * pantalla anuncia, o una parada de tabulación vacía entre dos enlaces. Aquí se
 * escriben una sola vez y no hay forma de pasarlos por alto.
 *
 * NUNCA SUSTITUYE AL TEXTO. Va siempre junto al nombre de la sección, que es de
 * donde sale el nombre accesible del enlace; el icono no aporta ni una palabra.
 */
function Icono({ de: Glifo }: { de: LucideIcon }) {
  return (
    <Glifo className="encabezado__icono" aria-hidden="true" focusable="false" />
  )
}

/**
 * El encabezado de toda la aplicación.
 *
 * POR QUÉ LA NAVEGACIÓN SE COLAPSA EN CELULAR. A 390px de ancho este encabezado
 * medía unos 440px de alto —escudo, título, tres enlaces y hasta seis botones de
 * sesión, todos apilados— antes de que apareciera una sola línea de contenido:
 * media pantalla de un teléfono gastada en el mismo mueble en cada ruta. Debajo
 * de 40rem se queda una barra compacta con el título y un botón «Menú», y lo
 * demás vive en un panel que se despliega.
 *
 * EL ESCUDO SE OCULTA EN CELULAR, NO SE ENCOGE. El Manual de Identidad Gráfica
 * fija 125px como ancho MÍNIMO para medios digitales (pág. 19), así que las dos
 * únicas opciones honestas son 125px o ausente; reducirlo sería incumplir el
 * manual. En un teléfono, 125px de escudo más su placa son casi el doble de alto
 * que la barra entera, y se paga en cada pantalla. Desde 40rem vuelve.
 *
 * EL DOM ES EL MISMO EN LOS DOS ANCHOS y lo único que cambia es CSS. Así ninguna
 * ruta, ninguna prueba y ningún lector de pantalla ven un árbol distinto según el
 * tamaño de la ventana, que es de donde salen las diferencias imposibles de
 * reproducir. En escritorio el panel es `display: contents`: desaparece como caja
 * y sus hijos vuelven a ser elementos directos del encabezado, exactamente como
 * antes de este cambio.
 */
export function Encabezado() {
  // El nombre de la institución sale de la configuración del servidor, NO del
  // código (ADR-0004: no hardcodear, para no bifurcar el repositorio por
  // escuela). Mientras carga no se muestra un nombre de relleno: poner
  // "Escuela de Artes" aquí sería volver al problema que esto resuelve.
  const { data: configuracion } = useQuery({
    queryKey: ['configuracion'],
    queryFn: obtenerConfiguracion,
    // Cambia poquísimo: no tiene sentido volver a pedirla en cada navegación.
    staleTime: 10 * 60 * 1000,
  })

  const dependencia = configuracion?.institucion_nombre

  const [menuAbierto, setMenuAbierto] = useState(false)
  const refBotonMenu = useRef<HTMLButtonElement | null>(null)
  const idPanel = useId()

  /* Navegar cierra el panel. Es el fallo clásico de un menú de teléfono: se
     pulsa un destino, la ruta cambia y el menú se queda encima tapando la
     pantalla a la que se acaba de llegar. Se escucha la dirección completa —no
     solo la ruta— porque «Disponibilidad» solo cambia la búsqueda. */
  const direccion = useRouterState({ select: (estado) => estado.location.href })
  useEffect(() => {
    setMenuAbierto(false)
  }, [direccion])

  /* Escape cierra y DEVUELVE EL FOCO al botón. Sin lo segundo, quien cierra con
     el teclado se queda con el foco en un enlace que acaba de desaparecer y el
     tabulador lo manda al principio del documento. */
  useEffect(() => {
    if (!menuAbierto) return

    function alTeclado(evento: KeyboardEvent) {
      if (evento.key !== 'Escape') return
      setMenuAbierto(false)
      refBotonMenu.current?.focus()
    }

    document.addEventListener('keydown', alTeclado)
    return () => document.removeEventListener('keydown', alTeclado)
  }, [menuAbierto])

  return (
    <header className="encabezado">
      <ESTILOS />
      <div className="encabezado__interior">
        {/*
          Escudo institucional de la UABC.

          Tres reglas del Manual de Identidad Gráfica condicionan cómo se
          coloca aquí, y están implementadas en `.encabezado__escudo` de
          global.css. No las relajes sin leer docs/analisis/16-identidad-uabc.md:

          - Ancho mínimo de 125 px en medios digitales (pág. 19). Por eso el
            encabezado es alto: el escudo no se puede encoger para que quepa.
          - Prohibido #11: la versión positiva no va sobre fondo oscuro, y el
            encabezado es verde institucional. Por eso el escudo se monta sobre
            una placa blanca, que además cumple el prohibido #10 (contorno
            blanco sobre fondos de color).
          - Prohibido #16: no estirarlo ni aplastarlo. El archivo es cuadrado
            (512×512) y se conserva la proporción 1:1.

          El texto guarda distancia por el prohibido #17.
        */}
        <img
          className="encabezado__escudo"
          src="/escudo-uabc.png"
          width={125}
          height={125}
          alt="Escudo de la Universidad Autónoma de Baja California"
        />

        <div className="encabezado__marca">
          <p className="encabezado__titulo">{NOMBRE_SISTEMA}</p>
          {/* El renglón se reserva aunque el nombre no haya llegado, para que
              el encabezado no salte de altura al cargar. */}
          <p className="encabezado__subtitulo">{dependencia ?? ' '}</p>
        </div>

        {/* Solo se ve debajo de 40rem; arriba de ese ancho la CSS lo oculta y
            con él desaparece del árbol de accesibilidad, que es lo correcto: en
            escritorio no hay nada que desplegar. */}
        <button
          type="button"
          className="encabezado__menu"
          ref={refBotonMenu}
          aria-expanded={menuAbierto}
          aria-controls={idPanel}
          onClick={() => setMenuAbierto((anterior) => !anterior)}
        >
          <span className="encabezado__hamburguesa" aria-hidden="true" />
          Menú
        </button>

        {/* El panel sigue INMEDIATAMENTE al botón en el orden del documento, así
            que al abrirlo el tabulador entra solo en su contenido y no hace
            falta mover el foco a mano: esto es un desplegable, no un diálogo
            modal, y robarle el foco a quien lo abrió sería peor. */}
        <div
          className="encabezado__panel"
          id={idPanel}
          data-abierto={menuAbierto ? 'true' : 'false'}
        >
          {/*
            CADA DESTINO CON SU ICONO, Y NINGUNO SIN SU PALABRA. El icono está
            para reconocer la sección de un vistazo —en la columna de escritorio
            son ocho destinos apilados, y una lista de ocho renglones de texto se
            lee entera cada vez—, pero el nombre accesible sigue saliendo del
            texto: los iconos van `aria-hidden`, así que para un lector de
            pantalla este menú es exactamente el mismo de antes.

            `focusable="false"` no es adorno: hay motores que meten los SVG en el
            orden de tabulación aunque no tengan nada que activar, y eso mete
            paradas muertas entre los enlaces.
          */}
          <nav className="navegacion" aria-label="Secciones">
            <Link to="/" activeOptions={{ exact: true }}>
              <Icono de={House} />
              Inicio
            </Link>
            <Link to="/espacios">
              <Icono de={DoorOpen} />
              Espacios
            </Link>
            {/* El enlace entra siempre al día de hoy. `includeSearch: false` es
                necesario para que la sección siga marcada como actual cuando el
                usuario ya cambió la fecha dentro de la pantalla. */}
            <Link
              to="/disponibilidad"
              search={{ fecha: fechaDeHoy() }}
              activeOptions={{ includeSearch: false }}
            >
              <Icono de={CalendarSearch} />
              Disponibilidad
            </Link>
          </nav>

          <Sesion />
        </div>
      </div>
    </header>
  )
}

/**
 * Los estilos del encabezado viajan con el encabezado, igual que en `Ayuda`,
 * `Tour` y `Esqueleto`: `precedence` monta una sola copia en el `<head>`. No hay
 * ni un color literal —todo sale de `tokens.css`— y la única consulta de ventana
 * usa el punto de quiebre de teléfono que ese archivo ya documenta, 40rem; no se
 * introduce ninguno nuevo.
 *
 * Los selectores llevan `.encabezado` delante a propósito: `global.css` ya trae
 * reglas para `.encabezado__interior` y `.encabezado__escudo` debajo de 34rem, y
 * ganarles por especificidad es lo que hace que el resultado no dependa de en
 * qué orden acaben las dos hojas dentro del documento.
 */
function ESTILOS() {
  return (
    <style href="cubiculos-encabezado" precedence="componente">{`
      /* Escritorio: el panel NO es una caja. Sus hijos vuelven a ser elementos
         directos del encabezado, y el reparto que global.css hace de la
         navegación y del bloque de sesión sigue funcionando tal cual. */
      .encabezado__panel { display: contents; }

      /* El enlace pasa a caja flexible para poder llevar icono y texto juntos.
         Con display: inline-block los dos se alinearían por la línea base y el
         icono, que es un cuadro sin línea base propia, quedaría hundido medio
         renglón. De paso, el justify-content que global.css ya aplicaba a estos
         mismos enlaces en la columna de escritorio por fin surte efecto: sobre
         una caja no flexible no hacía nada. */
      .encabezado .navegacion a,
      .encabezado .sesion a.boton {
        display: inline-flex;
        align-items: center;
        gap: var(--esp-2);
      }

      /* Medida fija en rem y no en em: el bloque de sesión y la navegación usan
         tamaños de texto distintos, y con em los iconos saldrían de dos
         tamaños en la misma columna. El color no se declara — se hereda por
         currentColor del enlace, que ya lo toma de los tokens, así que el tema
         oscuro y el contraste forzado lo siguen solos. El flex: none evita que
         un texto largo aplaste el icono hasta dejarlo ovalado. */
      .encabezado__icono {
        width: 1.125rem;
        height: 1.125rem;
        flex: none;
      }

      .encabezado__menu {
        display: none;
        font: inherit;
        font-size: var(--texto-sm);
        font-weight: 600;
        align-items: center;
        gap: var(--esp-2);
        margin-left: auto;
        padding: var(--esp-2) var(--esp-3);
        min-height: var(--tap-minimo);
        border: 1px solid currentColor;
        border-radius: var(--radio-sm);
        background-color: transparent;
        color: var(--texto-sobre-marca);
        cursor: pointer;
        transition: background-color var(--transicion-rapida);
      }

      /* Que esté desplegado se VE, además de anunciarse con aria-expanded: al
         desplazarse, el panel puede quedar fuera de la pantalla y entonces el
         botón es lo único que dice en qué estado está. */
      .encabezado__menu[aria-expanded='true'] {
        background-color: var(--color-marca-fondo-hover);
      }

      /* Las tres barras se dibujan con el color heredado: ni imagen que servir
         ni fuente de iconos, que es lo que se cae en el modo de contraste
         forzado justo cuando más falta hace ver el control. */
      .encabezado__hamburguesa {
        width: 1.125rem;
        height: 2px;
        background-color: currentColor;
        box-shadow: 0 -6px 0 currentColor, 0 6px 0 currentColor;
      }

      @media (max-width: 40rem) {
        /* El escudo se va entero. Ver el comentario de cabecera del archivo:
           125px es el MÍNIMO del manual, así que no existe una versión pequeña
           que sí se pueda usar. */
        .encabezado .encabezado__escudo { display: none; }

        /* La barra deja de centrarse: sin escudo, el título a la izquierda y el
           botón a la derecha es lo que se lee como barra. */
        .encabezado .encabezado__interior {
          justify-content: space-between;
          text-align: left;
          align-items: center;
          gap: var(--esp-3);
          padding-top: var(--esp-3);
          padding-bottom: var(--esp-3);
        }

        /* Sin esto, un título largo empuja el botón «Menú» fuera de la barra. */
        .encabezado .encabezado__marca {
          min-width: 0;
          flex: 1 1 auto;
        }

        .encabezado .encabezado__menu { display: inline-flex; }

        .encabezado .encabezado__panel {
          display: none;
          width: 100%;
          flex-direction: column;
          gap: var(--esp-3);
          margin-top: var(--esp-2);
          padding-top: var(--esp-3);
          border-top: 1px solid currentColor;
        }

        .encabezado .encabezado__panel[data-abierto='true'] { display: flex; }

        /* Un destino por renglón y a todo lo ancho. En fila envuelta los tres
           enlaces quedan de anchos distintos y con el pulgar se falla. */
        .encabezado .navegacion {
          flex-direction: column;
          align-items: stretch;
        }

        /* Se centra con justify-content y no solo con text-align: ahora el
           enlace es una caja flexible, y ahí text-align no mueve nada. */
        .encabezado .navegacion a {
          justify-content: center;
          text-align: center;
        }
      }
    `}</style>
  )
}

/**
 * Estado de sesión en el encabezado.
 *
 * LA REGLA QUE NO SE PUEDE ROMPER: con `autenticacion_disponible: false` no se
 * pinta ningún botón de entrar. Ese estado significa que el servidor no tiene
 * las variables de OAuth, y el botón acabaría en un 503. Hoy el despliegue está
 * exactamente así, o sea que este es el caso que se ve al desarrollar.
 *
 * Se reutiliza la misma consulta que el resto de la aplicación, así que
 * mostrarlo aquí no cuesta una petición extra.
 */
function Sesion() {
  const consulta = usePerfil()

  if (consulta.isPending) {
    return (
      <p className="sesion__nota" role="status">
        Comprobando tu sesión…
      </p>
    )
  }

  if (consulta.isError) {
    return (
      <p className="sesion__nota" role="status">
        No se pudo comprobar tu sesión: {textoDeError(consulta.error)}
      </p>
    )
  }

  const perfil = consulta.data

  if (perfil.estado === 'anonimo') {
    if (!perfil.autenticacion_disponible) {
      return (
        <p className="sesion__nota">
          El inicio de sesión todavía no está configurado en este servidor.
        </p>
      )
    }
    return (
      <div className="sesion">
        <BotonEntrar />
      </div>
    )
  }

  if (perfil.estado === 'sin_registrar') {
    return (
      <div className="sesion">
        {/* El nombre viene de Google y puede faltar; el correo nunca falta, y
            sirve igual para confirmar con qué cuenta se entró. */}
        <span className="sesion__quien">{perfil.nombre ?? perfil.correo}</span>
        <Link to="/registro" className="boton">
          Completar registro
        </Link>
        <BotonSalir />
      </div>
    )
  }

  return (
    <div className="sesion">
      <span className="sesion__quien">{perfil.usuario.correo}</span>
      {/* «Administración» solo se ofrece con el rol que puede entrar. No es una
          medida de seguridad —esa está en el API, que responde 403— sino de
          honestidad: enseñar una sección que va a rechazarte se lee como una
          aplicación rota. Hoy este enlace no lo ve nadie, porque el rol se
          asigna a mano en la base (ADR-0006). */}
      {/* El tablero de turno lo ven DOS roles: `mostrador` y `administrador`,
          que son los mismos que aceptan las funciones de la migración 015. Va
          antes que «Administración» porque para quien atiende es la pantalla de
          todo el día, no una sección a la que se entra de vez en cuando. */}
      {puedeAtenderMostrador(perfil.usuario) && (
        <Link to="/mostrador" className="boton">
          <Icono de={ConciergeBell} />
          Mostrador
        </Link>
      )}
      {puedeAdministrar(perfil.usuario) && (
        <Link to="/admin" className="boton">
          <Icono de={Settings} />
          Administración
        </Link>
      )}
      {/* «Mis reservaciones» solo aparece con registro completo, que es
          exactamente a quien la ruta deja entrar. Ofrecerlo antes llevaría a la
          pantalla de «completa tu registro» por un camino que no lo explica. */}
      <Campana />
      <Link to="/mis-reservaciones" className="boton">
        <Icono de={CalendarCheck} />
        Mis reservaciones
      </Link>
      <Link to="/mi-cuenta" className="boton">
        <Icono de={UserRound} />
        Mi cuenta
      </Link>
      <BotonSalir />
    </div>
  )
}

/**
 * La campana. Es lo que hace que un aviso se vea sin ir a buscarlo — y sin ella
 * el canal interno existiría sin que nadie se enterara.
 *
 * NO REVIENTA LA PÁGINA SI FALLA. Un error consultando avisos no debe tumbar el
 * encabezado entero: se pinta sin número, que es exactamente lo que significa
 * «no lo sé». Por eso `retry: false` y ninguna rama de error visible.
 *
 * Se refresca sola cada minuto. Es un recordatorio programado: si no se
 * revisara nunca, aparecería solo al recargar y llegaría tarde.
 */
function Campana() {
  const consulta = useQuery({
    queryKey: CLAVE_NOTIFICACIONES,
    queryFn: () => consultarAvisos(),
    retry: false,
    refetchInterval: 60_000,
  })

  const sinLeer = consulta.data?.sin_leer ?? 0

  return (
    <Link to="/avisos" className="boton">
      {/* La campana es el único icono del encabezado que además nombra la
          sección: «Avisos» con una campana al lado es lo que la gente busca
          cuando quiere saber si le llegó algo. El texto sigue mandando; el
          icono es mudo para el árbol de accesibilidad. */}
      <Icono de={Bell} />
      Avisos
      {sinLeer > 0 && (
        <span className="campana__cuenta" aria-hidden="true">
          {sinLeer}
        </span>
      )}
      {/* El número se anuncia con palabras: un «3» suelto no dice de qué. */}
      {sinLeer > 0 && (
        <span className="visualmente-oculto">
          , {sinLeer} sin leer
        </span>
      )}
    </Link>
  )
}
